import React, { useState, useEffect } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { Calendar as CalendarIcon, Plus, Search, Bell, Download, Filter, X } from 'lucide-react';
import axios from 'axios';
import AppointmentDayModal from './AppointmentDayModal';
import AddAppointmentModal from './AddAppointmentModal';
import NotificationsModal from './NotificationsModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AppointmentCalendar = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [appointmentsByDate, setAppointmentsByDate] = useState({});
  const [showDayModal, setShowDayModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const [upcomingCount, setUpcomingCount] = useState(0);

  useEffect(() => {
    fetchAppointments();
    fetchUpcomingCount();
  }, []);

  const fetchAppointments = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/emr/appointments/calendar`, {
        withCredentials: true
      });
      if (response.data.appointments) {
        setAppointmentsByDate(response.data.appointments);
      }
    } catch (error) {
      console.error('Failed to fetch appointments:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUpcomingCount = async () => {
    try {
      const response = await axios.get(`${API}/emr/appointments/upcoming?days=7`, {
        withCredentials: true
      });
      if (response.data.count) {
        setUpcomingCount(response.data.count);
      }
    } catch (error) {
      console.error('Failed to fetch upcoming:', error);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      const response = await axios.get(
        `${API}/emr/appointments/search?query=${encodeURIComponent(searchQuery)}`,
        { withCredentials: true }
      );
      setSearchResults(response.data.results || []);
      setShowSearch(true);
    } catch (error) {
      console.error('Search failed:', error);
    }
  };

  const exportToPDF = async () => {
    try {
      const response = await axios.get(`${API}/emr/appointments/export/pdf`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `appointments_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Export failed:', error);
      alert('Failed to export PDF');
    }
  };

  const handleDateClick = (date) => {
    setSelectedDate(date);
    setShowDayModal(true);
  };

  const handleAddAppointment = () => {
    setShowAddModal(true);
  };

  const handleAppointmentAdded = () => {
    fetchAppointments(); // Refresh calendar
    setShowAddModal(false);
  };

  const getTileContent = ({ date, view }) => {
    if (view === 'month') {
      const dateStr = date.toLocaleDateString('en-US', { 
        month: 'long', 
        day: 'numeric', 
        year: 'numeric' 
      });
      
      const appointments = appointmentsByDate[dateStr] || [];
      
      if (appointments.length > 0) {
        return (
          <div className="flex justify-center mt-1">
            <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
          </div>
        );
      }
    }
    return null;
  };

  return (
    <div className="bg-gray-900 border border-yellow-500/20 rounded-2xl p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <CalendarIcon className="w-8 h-8 text-yellow-400" />
          <h2 className="text-2xl font-bold text-yellow-400">Appointment Schedule</h2>
        </div>
        <div className="flex items-center space-x-2">
          {/* Search */}
          <div className="relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Search appointments..."
              className="px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:ring-2 focus:ring-yellow-400 pr-10"
            />
            <button
              onClick={handleSearch}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-yellow-400"
            >
              <Search className="w-5 h-5" />
            </button>
          </div>

          {/* Notifications */}
          <button
            onClick={() => setShowNotifications(true)}
            className="relative p-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
            title="Upcoming Appointments"
          >
            <Bell className="w-5 h-5 text-yellow-400" />
            {upcomingCount > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                {upcomingCount}
              </span>
            )}
          </button>

          {/* Export PDF */}
          <button
            onClick={exportToPDF}
            className="p-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
            title="Export to PDF"
          >
            <Download className="w-5 h-5 text-yellow-400" />
          </button>

          {/* Add Appointment */}
          <button
            onClick={handleAddAppointment}
            className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-black font-semibold rounded-lg transition-all"
          >
            <Plus className="w-5 h-5" />
            <span>Add</span>
          </button>
        </div>
      </div>

      {/* Search Results */}
      {showSearch && searchResults.length > 0 && (
        <div className="mb-4 bg-black/30 border border-yellow-500/30 rounded-lg p-4 max-h-48 overflow-y-auto">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-white font-semibold">Search Results ({searchResults.length})</h3>
            <button
              onClick={() => setShowSearch(false)}
              className="text-gray-400 hover:text-white"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="space-y-2">
            {searchResults.map((result, idx) => (
              <div
                key={idx}
                className="bg-gray-800 p-3 rounded-lg cursor-pointer hover:bg-gray-700"
                onClick={() => {
                  const date = new Date(result.appointment_date);
                  setSelectedDate(date);
                  handleDateClick(date);
                  setShowSearch(false);
                }}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-white font-medium">{result.patient_name}</p>
                    <p className="text-gray-400 text-sm">{result.service}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-yellow-400 text-sm">{result.appointment_date}</p>
                    <p className="text-gray-400 text-xs">{result.appointment_time}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="text-gray-400">Loading appointments...</div>
        </div>
      ) : (
        <div className="appointment-calendar">
          <Calendar
            onChange={handleDateClick}
            value={selectedDate}
            tileContent={getTileContent}
            className="bg-gray-800 text-white border-gray-700 rounded-xl"
          />
        </div>
      )}

      <p className="text-gray-400 text-sm mt-4 text-center">
        <span className="inline-flex items-center">
          <span className="w-2 h-2 bg-yellow-400 rounded-full mr-2"></span>
          = Appointments scheduled
        </span>
      </p>

      {showDayModal && (
        <AppointmentDayModal
          date={selectedDate}
          onClose={() => setShowDayModal(false)}
          onRefresh={fetchAppointments}
        />
      )}

      {showAddModal && (
        <AddAppointmentModal
          onClose={() => setShowAddModal(false)}
          onSuccess={handleAppointmentAdded}
        />
      )}

      <style jsx>{`
        .appointment-calendar :global(.react-calendar) {
          width: 100%;
          border: none;
          background: transparent;
          font-family: inherit;
        }
        
        .appointment-calendar :global(.react-calendar__tile) {
          background: rgba(31, 41, 55, 0.5);
          color: white;
          border: 1px solid rgba(75, 85, 99, 0.3);
          border-radius: 8px;
          padding: 12px;
          margin: 3px;
          transition: all 0.2s;
        }
        
        .appointment-calendar :global(.react-calendar__tile:hover) {
          background: rgba(250, 204, 21, 0.1);
          border-color: rgba(250, 204, 21, 0.3);
        }
        
        .appointment-calendar :global(.react-calendar__tile--active) {
          background: rgba(250, 204, 21, 0.2);
          border-color: #FACC15;
        }
        
        .appointment-calendar :global(.react-calendar__tile--now) {
          background: rgba(59, 130, 246, 0.2);
          border-color: rgba(59, 130, 246, 0.5);
        }
        
        .appointment-calendar :global(.react-calendar__month-view__weekdays) {
          color: #FACC15;
          font-weight: 600;
          text-transform: uppercase;
          font-size: 0.75rem;
        }
        
        .appointment-calendar :global(.react-calendar__navigation button) {
          color: white;
          background: rgba(31, 41, 55, 0.5);
          border-radius: 8px;
          font-weight: 600;
        }
        
        .appointment-calendar :global(.react-calendar__navigation button:hover) {
          background: rgba(250, 204, 21, 0.1);
        }
      `}</style>

      {/* Notifications Modal */}
      {showNotifications && (
        <NotificationsModal
          onClose={() => setShowNotifications(false)}
          onRefresh={fetchUpcomingCount}
        />
      )}
    </div>
  );
};

export default AppointmentCalendar;
